import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AgGridTree from '@/app/common/components/ag-grid/AgGridTree';
import { decodeName } from '../utils/decodeName';
import { buildLeafSeoIndex } from '../utils/leafSeoIndex';
import { clusterTreeSeoColumnDefs } from '../config/clusterTreeSeoColumnDefs';
import ClusterExportToolbar from './ClusterExportToolbar';
import type {
  ClusterData,
  ClusterModeId,
  ClusterTableRow,
  VolumeThresholds
} from '../types';

type ClusterTreeRow = {
  name: string[];
  volume: number | null;
  difficulty: number | null;
  len: number | null;
  intent: string[];
  purpose: string[];
};

type AgGridTreeHandle = {
  expandAll: () => void;
  collapseAll: () => void;
};

type ClusterTableProps = {
  rows?: ClusterTableRow[];
  clusterData?: ClusterData | null;
  clusterMode?: ClusterModeId | string;
  onClusterModeChange?: (mode: ClusterModeId | string) => void;
  clusterSummary?: string | null;
  thresholds?: VolumeThresholds | null;
};

export default function ClusterTable({
  rows = [],
  clusterData,
  clusterMode = 'default',
  onClusterModeChange,
  clusterSummary = '',
  thresholds = null
}: ClusterTableProps) {
  const gridRef = useRef<AgGridTreeHandle | null>(null);
  // AgGridTree expands all rows on ready.
  const [allExpanded, setAllExpanded] = useState(true);

  const leafSeo = useMemo(
    () => buildLeafSeoIndex(clusterData?.hierarchical),
    [clusterData]
  );

  const treeRows = useMemo<ClusterTreeRow[]>(
    () =>
      rows.map(row => {
        const path = row.name ?? [];
        const leaf = decodeName(path[path.length - 1] || '');
        const seo = leafSeo.get(leaf) || null;
        return {
          name: path,
          volume: seo?.search_volume_miztols ?? null,
          difficulty: seo?.difficulty ?? null,
          len: seo?.len ?? null,
          intent: seo?.intent || [],
          purpose: seo?.purpose || []
        };
      }),
    [leafSeo, rows]
  );

  useEffect(() => {
    setAllExpanded(true);
  }, [clusterMode, rows]);

  const handleToggleExpandAll = useCallback(() => {
    const api = gridRef.current;
    if (!api) return;
    if (allExpanded) {
      api.collapseAll();
      setAllExpanded(false);
    } else {
      api.expandAll();
      setAllExpanded(true);
    }
  }, [allExpanded]);

  return (
    <div className="d-flex flex-column gap-3">
      <ClusterExportToolbar
        rows={rows}
        clusterData={clusterData}
        clusterMode={clusterMode}
        onClusterModeChange={onClusterModeChange}
        pathCount={rows.length}
        clusterSummary={clusterSummary}
        thresholds={thresholds}
        allExpanded={allExpanded}
        onToggleExpandAll={handleToggleExpandAll}
      />

      <AgGridTree
        ref={gridRef}
        data={treeRows as never[]}
        height="650px"
        columnDefs={clusterTreeSeoColumnDefs as never[]}
        autoSizeOnReady={false}
        fitColumns
        autoGroupColumnDef={{
          flex: 3,
          minWidth: 240
        }}
      />
    </div>
  );
}
